import { DateFormatter, Translate } from '@components'

import { TableColumn } from '@billy/design-system'
import { useMemo } from 'react'

import {
  IMPORTED_INVOICE_SYNC_STATUS_WIDTH,
  ImportedInvoiceSyncStatus,
} from '@features/importedInvoices/elements/ImportedInvoiceSyncStatus'

import { ImportedInvoicesSortProperty } from '../../../../../enums/importedInvoicesSortProperty'
import { ImportedInvoicesTableRow } from '../../../../../types/importedInvoicesTableRow'
import { importedInvoicesTableColumns } from '../../../constants/importedInvoicesTableColumns'
import { ImportedInvoicesTableAccessor } from '../../../enums/importedInvoicesTableAccessor'

export const useImportedInvoicesTableColumns = () => {
  const columns: TableColumn<ImportedInvoicesTableRow>[] = useMemo(
    () =>
      importedInvoicesTableColumns.map((column) => {
        switch (column) {
          case ImportedInvoicesTableAccessor.FileName:
            return {
              accessor: ImportedInvoicesTableAccessor.FileName,
              Header: <Translate value="imported_invoices.table.column.file_name" />,
              size: 'm',
              truncate: true,
            }
          case ImportedInvoicesTableAccessor.UploadDate:
            return {
              accessor: ImportedInvoicesTableAccessor.UploadDate,
              Cell: ({ value: uploadDate }) => uploadDate && <DateFormatter value={uploadDate} />,
              Header: <Translate value="imported_invoices.table.column.upload_date" />,
              // The DS table sends the column id back as the API sort key, and `getColumns`
              // only falls back to the accessor when no id is set — the accessor has to stay
              // the row data key, so the sort key is set explicitly here.
              id: ImportedInvoicesSortProperty.UploadDate,
              size: 'm',
              sortable: true,
            }
          case ImportedInvoicesTableAccessor.IntegrationStatus:
            // Raw `width`/`maxWidth` rather than a `size` token so the column lands on
            // the design's exact 105px — `getColumns` spreads `sizeToWidth[size]` after
            // `...rest`, so a token here would silently overwrite `maxWidth`.
            return {
              accessor: ImportedInvoicesTableAccessor.IntegrationStatus,
              alignment: 'center',
              Cell: ({ row, value: integrationStatus }) => (
                <ImportedInvoiceSyncStatus rejectCode={row.original.integrationRejectCode} status={integrationStatus} />
              ),
              fixedSize: true,
              Header: <Translate value="imported_invoices.table.column.status" />,
              maxWidth: IMPORTED_INVOICE_SYNC_STATUS_WIDTH,
              renderAsChildren: true,
              width: IMPORTED_INVOICE_SYNC_STATUS_WIDTH,
            }
          default:
            throw new Error(`Unsupported column: ${column}`)
        }
      }),
    [],
  )

  return { columns }
}
